/**
 * Trip status constants and utilities
 * Trip status is calculated based on embarkation and disembarkation dates
 * - Upcoming: Trip hasn't started yet (embarkationDate > now)
 * - Ongoing: Trip is currently happening (embarkationDate <= now <= disembarkationDate)
 * - Past: Trip has ended (disembarkationDate < now)
 */

import type { ChipProps } from '@mui/material';

/**
 * Trip status type
 */
export type TripStatus = 'upcoming' | 'ongoing' | 'past';

/**
 * Trip status configuration
 * Maps trip status to display label and Material-UI Chip color
 */
export const TRIP_STATUS_MAP = {
  upcoming: {
    label: 'Upcoming',
    color: 'success' as ChipProps['color'],
  },
  ongoing: {
    label: 'Ongoing',
    color: 'info' as ChipProps['color'],
  },
  past: {
    label: 'Past',
    color: 'default' as ChipProps['color'],
  },
} as const;

/**
 * Status configuration type
 */
export type TripStatusConfig = (typeof TRIP_STATUS_MAP)[TripStatus];

/**
 * Trip date information required for status calculation
 */
export interface TripDateInfo {
  embarkationDate: string | Date;
  disembarkationDate: string | Date;
}

/**
 * Get trip status based on embarkation and disembarkation dates
 * Calculates whether a trip is upcoming, ongoing, or past
 *
 * @param trip - Trip object with embarkationDate and disembarkationDate
 * @param referenceDate - Optional reference date (defaults to current date)
 * @returns Trip status configuration with label and color
 *
 * @example
 * ```ts
 * const status = getTripStatus({
 *   embarkationDate: '2024-12-01',
 *   disembarkationDate: '2024-12-07'
 * });
 * // Returns: { label: 'Upcoming', color: 'success' }
 * ```
 */
export const getTripStatus = (
  trip: TripDateInfo,
  referenceDate: Date = new Date(),
): TripStatusConfig => {
  const embarkDate = new Date(trip.embarkationDate);
  const disembarkDate = new Date(trip.disembarkationDate);

  if (embarkDate > referenceDate) {
    return TRIP_STATUS_MAP.upcoming;
  } else if (disembarkDate < referenceDate) {
    return TRIP_STATUS_MAP.past;
  } else {
    return TRIP_STATUS_MAP.ongoing;
  }
};

/**
 * Check if a trip is upcoming
 *
 * @param trip - Trip object with embarkationDate and disembarkationDate
 * @param referenceDate - Optional reference date (defaults to current date)
 * @returns True if trip is upcoming
 */
export const isTripUpcoming = (
  trip: TripDateInfo,
  referenceDate: Date = new Date(),
): boolean => {
  return new Date(trip.embarkationDate) > referenceDate;
};

/**
 * Check if a trip is ongoing
 *
 * @param trip - Trip object with embarkationDate and disembarkationDate
 * @param referenceDate - Optional reference date (defaults to current date)
 * @returns True if trip is ongoing
 */
export const isTripOngoing = (
  trip: TripDateInfo,
  referenceDate: Date = new Date(),
): boolean => {
  const embarkDate = new Date(trip.embarkationDate);
  const disembarkDate = new Date(trip.disembarkationDate);
  return embarkDate <= referenceDate && disembarkDate >= referenceDate;
};

/**
 * Check if a trip is past
 *
 * @param trip - Trip object with embarkationDate and disembarkationDate
 * @param referenceDate - Optional reference date (defaults to current date)
 * @returns True if trip is past
 */
export const isTripPast = (
  trip: TripDateInfo,
  referenceDate: Date = new Date(),
): boolean => {
  return new Date(trip.disembarkationDate) < referenceDate;
};

/**
 * Array of all trip statuses
 * Useful for iteration or filtering
 */
export const TRIP_STATUSES: TripStatus[] = ['upcoming', 'ongoing', 'past'];

/**
 * Trip status display order
 * Defines the order in which statuses should be displayed in UI
 */
export const TRIP_STATUS_ORDER: TripStatus[] = ['upcoming', 'ongoing', 'past'];
